type BootLoadingTone = "info" | "success" | "error";

interface BootLoadingEntry {
  readonly label: string;
  readonly message: string;
  readonly tone: BootLoadingTone;
}

interface BootLoadingRefs {
  readonly stage: HTMLDivElement;
  readonly frame: HTMLDivElement;
  readonly root: HTMLDivElement;
  readonly progressLine: HTMLDivElement;
  readonly statusLine: HTMLDivElement;
  readonly log: HTMLDivElement;
}

interface WindowWithSignalBootShell extends Window {
  __SIGNAL_BOOT_SHELL__?: {
    stop?: () => void;
    handoff?: (progress: number, message: string) => void;
  };
}

const MAX_LOG_LINES = 10;
const HIDE_DELAY_MS = 180;

let refs: BootLoadingRefs | null = null;
let entries: readonly BootLoadingEntry[] = [];
let state: "booting" | "ready" | "failed" = "booting";
let progressValue = 3;

function parseInitialEntry(text: string): BootLoadingEntry {
  const match = /^\[([A-Z]+)\]\s*(.*)$/u.exec(text.trim());
  if (match === null) {
    return {
      label: "BOOT",
      message: text,
      tone: "info",
    };
  }

  return {
    label: match[1] ?? "BOOT",
    message: match[2] ?? "",
    tone: "info",
  };
}

function ensureRefs(): BootLoadingRefs {
  if (refs !== null) {
    return refs;
  }

  const root = document.querySelector<HTMLDivElement>("#boot-loading-root");
  const stage = document.querySelector<HTMLDivElement>("#boot-shell-stage");
  const frame = document.querySelector<HTMLDivElement>(".boot-shell-stage__frame");
  const progressLine = root?.querySelector<HTMLDivElement>("[data-role=\"progress-line\"]");
  const statusLine = root?.querySelector<HTMLDivElement>("[data-role=\"status-line\"]");
  const log = root?.querySelector<HTMLDivElement>("[data-role=\"log\"]");
  if (root === null || log === null || stage === null || frame === null || progressLine === null || statusLine === null) {
    throw new Error("Boot loading shell was not found.");
  }

  entries = Array.from(log.querySelectorAll<HTMLElement>(".boot-loading__log-line")).map((line) =>
    parseInitialEntry(line.textContent ?? ""),
  );
  progressValue = Number(progressLine.dataset["progress"] ?? "3");

  refs = { stage, frame, root, progressLine, statusLine, log };
  return refs;
}

function stopEarlyAnimation(): void {
  (window as WindowWithSignalBootShell).__SIGNAL_BOOT_SHELL__?.stop?.();
}

function renderLogs(): void {
  const { log } = ensureRefs();
  log.replaceChildren();

  for (const entry of entries) {
    const line = document.createElement("div");
    line.className = `boot-loading__log-line boot-loading__log-line--${entry.tone}`;
    line.textContent = `[${entry.label}] ${entry.message}`;
    log.appendChild(line);
  }
}

function renderProgressLine(): void {
  const { progressLine } = ensureRefs();
  progressLine.dataset["progress"] = String(progressValue);
  progressLine.textContent = `[PROG] ${String(progressValue).padStart(2, "0")}% pipeline synchronized`;
}

function renderStatusLine(message: string): void {
  const { statusLine } = ensureRefs();
  statusLine.textContent = `[LOAD] ${message}`;
}

function appendEntry(entry: BootLoadingEntry): void {
  entries = [...entries, entry].slice(-MAX_LOG_LINES);
  renderLogs();
}

function canMutate(): boolean {
  return state === "booting";
}

export function mountBootLoadingShell(): void {
  const shell = ensureRefs();
  (window as WindowWithSignalBootShell).__SIGNAL_BOOT_SHELL__?.handoff?.(
    progressValue,
    "[LOAD] runtime bridge attached",
  );
  shell.root.style.display = "";
  shell.root.classList.remove("is-hidden");
  shell.root.dataset["state"] = state;
  shell.stage.style.display = "";
  renderProgressLine();
  renderStatusLine("runtime bridge attached");
  renderLogs();
}

export function syncBootLoadingShellFrame(layout: {
  readonly width: number;
  readonly height: number;
  readonly topOffset: number;
}): void {
  const shell = ensureRefs();
  shell.stage.style.alignItems = "flex-start";
  shell.stage.style.paddingTop = `${Math.max(0, layout.topOffset)}px`;
  shell.frame.style.width = `${layout.width}px`;
  shell.frame.style.height = `${layout.height}px`;
}

export function pushBootLoadingLog(
  label: string,
  message: string,
  tone: BootLoadingTone = "info",
): void {
  if (!canMutate()) {
    return;
  }

  appendEntry({ label, message, tone });
}

export function setBootLoadingProgress(progress: number, statusMessage?: string): void {
  if (!canMutate()) {
    return;
  }

  progressValue = Math.max(progressValue, Math.min(100, Math.max(0, Math.round(progress))));
  renderProgressLine();
  if (statusMessage !== undefined) {
    renderStatusLine(statusMessage);
  }
}

export function markBootLoadingReady(): void {
  if (!canMutate()) {
    return;
  }

  state = "ready";
  const shell = ensureRefs();
  shell.root.dataset["state"] = "ready";
  shell.root.classList.add("is-hidden");
  stopEarlyAnimation();

  window.setTimeout(() => {
    shell.root.style.display = "none";
    shell.stage.style.display = "none";
  }, HIDE_DELAY_MS);
}

export function markBootLoadingFailed(message: string, detail?: string): void {
  if (!canMutate()) {
    return;
  }

  state = "failed";
  const shell = ensureRefs();
  shell.root.dataset["state"] = "failed";
  stopEarlyAnimation();
  appendEntry({ label: "ERR", message, tone: "error" });
  if (detail !== undefined && detail.trim().length > 0) {
    appendEntry({ label: "DET", message: detail, tone: "error" });
  }
}
